/**
 * WEB-1993 п.2 — очередь событий аналитики, снимающая отправку с пути клика.
 *
 * ЗАЧЕМ. INP (задержка между тапом и следующей отрисовкой) меряется по самому
 * долгому взаимодействию: всё, что исполняется синхронно в обработчике тапа,
 * отодвигает кадр. Прежний `trackEvent` в момент тапа разбирал адрес и куки,
 * собирал тело через `JSON.stringify` и открывал `fetch` — работа небольшая,
 * но лежащая ровно между пальцем и кадром, и повторяемая по нескольку раз на
 * один тап (SCREEN_ANSWER на каждый отвеченный блок + QUIZ_START).
 *
 * КАК. Вызов кладёт в очередь адрес и ФУНКЦИЮ сборки тела (сериализация тоже
 * уезжает с пути клика) и возвращается. Слив — после отрисовки:
 * `requestIdleCallback` с таймаутом (браузер сам выберет простой, но не позже
 * таймаута), иначе следующий кадр `requestAnimationFrame` + макрозадание,
 * иначе просто макрозадание.
 *
 * ЧТО НЕ МЕНЯЕТСЯ. Состав тела и ПОРЯДОК запросов: очередь FIFO, слив идёт
 * одним проходом, по одному запросу на событие — ровно как раньше, когда
 * каждый вызов открывал свой `fetch`. Приёмный формат бэкенда (один объект
 * события на запрос) не трогаем: пакетной ручки у него нет.
 *
 * ХВОСТ ПРИ УХОДЕ. `visibilitychange` → `hidden` и `pagehide` досылают
 * накопленное через `navigator.sendBeacon` (переживает уход со страницы), а
 * если его нет или он отказал — `fetch` с `keepalive`. Плюс явный
 * `flushAnalyticsQueue({ transport: 'beacon' })` перед собственным редиректом
 * плеера: там страница уходит по нашей же команде и ждать событие
 * `visibilitychange` не на что.
 */

/** Отложенная сборка тела: сериализация уезжает с пути клика вместе с сетью. */
export type AnalyticsBodyBuilder = () => string;

interface QueuedRequest {
  url: string;
  buildBody: AnalyticsBodyBuilder;
}

/** Потолок ожидания простоя: дольше держать событие в памяти незачем. */
export const ANALYTICS_IDLE_TIMEOUT_MS = 1000;

const queue: QueuedRequest[] = [];
let flushScheduled = false;
let unloadHooked = false;

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
};

/**
 * WEB-1364 п.5 (вложенный подпункт) — отказ доставки события больше не молчит.
 *
 * Ответ сервера здесь не читался вовсе (`.catch(()=>{})`), поэтому целый класс
 * отказов был невидим: например 400-я на событие посетителя с негодным полем.
 * Это минимальная отладочная запись — не тревога и не повтор отправки: наружу
 * ничего не бросается, покупателю ничего не мешает.
 *
 * Строка на английском намеренно: это запись в консоли разработчика, а не
 * текст интерфейса (правило двух языков — про то, что читает клиент).
 */
function warnDeliveryFailed(url: string, reason: string): void {
  try {
    console.warn(`[analytics] event delivery failed: ${reason} — ${url}`);
  } catch {
    /* консоли может не быть — молчим */
  }
}

/**
 * Отправка через `fetch` с `keepalive`. Возвращает false, если вызвать fetch
 * не удалось вовсе (его нет в окружении / бросил синхронно) — тогда у
 * вызывающего остаётся запасной путь.
 */
function sendViaFetch(url: string, body: string): boolean {
  try {
    const promise = fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      // WEB-1257 (хвост WEB-1403): без credentials кука qb_visitor_id не
      // прикладывалась даже на наших поддоменах — вариант A/B события
      // резолвился мимо закрепления. Перенесено без изменений.
      credentials: 'include',
    });
    promise
      ?.then?.((response) => {
        if (!response) return;
        if (response.ok === false) {
          warnDeliveryFailed(url, `HTTP ${response.status}`);
          return;
        }
        // WEB-2199: ручки приёма отвечают `{success:true, recorded:false}`,
        // когда событие НЕ записано (например, распознан бот). Код при этом
        // 200 — без разбора тела такой отказ выглядит успехом.
        void response
          .json?.()
          ?.then?.((body: unknown) => {
            if (
              body &&
              typeof body === 'object' &&
              (body as { recorded?: unknown }).recorded === false
            ) {
              warnDeliveryFailed(url, 'not recorded by the server');
            }
          })
          ?.catch?.(() => {
            /* тело не разобралось — это не отказ доставки, молчим */
          });
      })
      ?.catch?.((error: unknown) => {
        // Только класс сбоя, не текст: сырой текст ошибки на экран/в консоль
        // не печатаем (сторож WEB-1874), а для разбора «не дошло» довольно имени.
        warnDeliveryFailed(
          url,
          error instanceof Error ? error.name : 'network error',
        );
      });
    return true;
  } catch {
    /* аналитика никогда не мешает покупателю */
    return false;
  }
}

/**
 * Ревизия пачки 29, п.12 — СВОЁ происхождение или чужое.
 *
 * Маяк с телом `Blob[application/json]` — запрос НЕ из простых: на чужое
 * происхождение браузер обязан сперва сходить за разрешением (предварительный
 * запрос OPTIONS). На уходе со страницы на этот круг может не остаться
 * времени, и последнее событие — в том числе денежное — не уходит вовсе и
 * молча. Поэтому на чужое происхождение первым идёт `fetch` с `keepalive`
 * (переживает уход по спецификации и несёт куку через `credentials`).
 *
 * Разбор адреса не должен ронять аналитику: не разобрался — считаем своим и
 * ведём себя как раньше.
 */
function isSameOrigin(url: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.location) return true;
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return true;
  }
}

function sendViaBeacon(url: string, body: string): void {
  // Чужое происхождение — см. isSameOrigin: маяк там платит предварительным
  // запросом, которого на уходе может не хватить. Не удалось позвать fetch
  // вовсе — падаем на маяк ниже, событие важнее способа доставки.
  if (!isSameOrigin(url) && sendViaFetch(url, body)) return;
  try {
    const beacon = navigator?.sendBeacon;
    if (typeof beacon === 'function') {
      // Отказ маяка (превышена квота, выключен) — не потеря события:
      // тем же вызовом падаем обратно на fetch с keepalive.
      const ok = beacon.call(
        navigator,
        url,
        new Blob([body], { type: 'application/json' }),
      );
      if (ok) return;
    }
  } catch {
    /* ниже — запасной путь */
  }
  sendViaFetch(url, body);
}

/**
 * Слив очереди. `transport: 'beacon'` — для ухода со страницы.
 * Очередь опустошается ДО отправки: повторный слив ничего не дублирует.
 */
export function flushAnalyticsQueue(
  options: { transport?: 'fetch' | 'beacon' } = {},
): void {
  flushScheduled = false;
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const viaBeacon = options.transport === 'beacon';
  for (const item of batch) {
    let body: string;
    try {
      body = item.buildBody();
    } catch {
      // Сломанная сборка тела одного события не должна ронять остальные.
      continue;
    }
    if (viaBeacon) sendViaBeacon(item.url, body);
    else sendViaFetch(item.url, body);
  }
}

function hookUnloadOnce(): void {
  if (unloadHooked) return;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  unloadHooked = true;
  const flushTail = () => {
    if (queue.length > 0) flushAnalyticsQueue({ transport: 'beacon' });
  };
  document.addEventListener('visibilitychange', () => {
    // Только уход: возврат на вкладку сливать незачем.
    if (document.visibilityState === 'hidden') flushTail();
  });
  // pagehide — единственное надёжное событие на iOS Safari (bfcache).
  window.addEventListener('pagehide', flushTail);
}

function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  const run = () => flushAnalyticsQueue();
  if (typeof window === 'undefined') {
    flushScheduled = false;
    flushAnalyticsQueue();
    return;
  }
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(run, { timeout: ANALYTICS_IDLE_TIMEOUT_MS });
    return;
  }
  if (typeof w.requestAnimationFrame === 'function') {
    // Кадр отрисовки — сначала пиксели покупателю, потом сеть.
    w.requestAnimationFrame(() => setTimeout(run, 0));
    return;
  }
  setTimeout(run, 0);
}

/**
 * Положить запрос в очередь. Синхронная часть — один `push`: ни сети, ни
 * сериализации на пути клика.
 */
export function enqueueAnalyticsRequest(
  url: string,
  buildBody: AnalyticsBodyBuilder,
): void {
  queue.push({ url, buildBody });
  hookUnloadOnce();
  scheduleFlush();
}

export function __resetAnalyticsQueueForTests(): void {
  queue.length = 0;
  flushScheduled = false;
}
